/**
 * Centralized React Query key registry — single source of truth.
 *
 * Importing constants from here (instead of writing string literals at the
 * call-site) makes typos a compile-time error and guarantees that mutations
 * invalidate the same keys their queries read from.
 *
 * RULE: never write `queryKey: ['some-string']` inline anywhere else.
 *       Add it here, then import the constant.
 *
 * NOTE: The string values intentionally mirror the keys that were already in
 * use across the codebase before this refactor. Renaming a string would
 * silently bust the React Query cache for users mid-session, so the values
 * stay stable and only the references are centralized.
 */
export const queryKeys = {
  // Auth / user
  profile: ['profile'] as const,
  userRoles: ['userRoles'] as const,

  // Workouts
  workouts: ['workouts'] as const,
  workoutById: (id: string) => ['workouts', id] as const,
  workoutsTotalCount: ['workouts_total_count'] as const,
  
  weeklyWorkoutStats: ['weeklyWorkoutStats'] as const,
  
  prDetection: ['prDetection'] as const,
  exerciseProgressions: ['exercise-progressions'] as const,
  exercises: ['exercises'] as const,

  // Routines
  routines: ['workout-routines'] as const,
  routineById: (id: string) => ['workout-routines', id] as const,

  // Nutrition
  foodEntries: ['foodEntries'] as const,
  
  customFoods: ['customFoods'] as const,
  recipes: ['recipes'] as const,
  waterEntries: ['waterEntries'] as const,
  
  moodLogs: ['moodLogs'] as const,


  // Progress
  weightEntries: ['weightEntries'] as const,
  bodyMeasurements: ['body-measurements'] as const,
  progressPhotos: ['progress-photos'] as const,
  signedUrl: (path: string) => ['signed-url', path] as const,

  // Coach
  chatMessages: ['chat_messages'] as const,
  coachMemories: ['coach-memories'] as const,
  coachObservations: ['coach-observations'] as const,

  // HYROX
  hyroxTotalCount: ['hyrox_total_count'] as const,

  // Coach check-ins
  coachCheckins: ['coach-checkins'] as const,
  latestWeeklyCheckin: ['latest-weekly-checkin'] as const,
  latestMonthlyReport: ['latest-monthly-report'] as const,

  // Coach programmes
  activeProgram: ['active-program'] as const,
  programUpdates: ['program-updates'] as const,


  // Subscription
  subscriptionStatus: ['subscription-status'] as const,
  coachMessageCount: ['coach-message-count'] as const,

  // Settings
  notificationPreference: (type: string) =>
    ['notification-preference', type] as const,

  // Accountability nudges
  profileAccountability: ['profile-accountability'] as const,
  recentWorkoutsAccountability: ['recent-workouts-accountability'] as const,
  latestWeightAccountability: ['latest-weight-accountability'] as const,
  todayActivityAccountability: ['today-activity-accountability'] as const,
} as const;
